import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
  Bar,
  Line,
  Cell,
  PieChart,
  Pie
} from "recharts";
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaClock,
  FaMoneyBillWave,
  FaUserTie,
  FaCalculator,
  FaSlidersH
} from "react-icons/fa";
import { getData } from "../../services/FetchNodeservices";

export default function TalentIntelligence() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Multi-dimensional matrix selectors
  const [dimA, setDimA] = useState("location");
  const [dimB, setDimB] = useState("designation");
  const [trendMode, setTrendMode] = useState("median"); // "median" or "distribution"

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      let res = await getData("api/talentpool/insights");
      if (!res || res.status === undefined) {
        res = await getData("talentpool/insights");
      }
      if (res?.status) {
        setData(res.data);
      } else {
        throw new Error(res?.message || "Failed to load insights");
      }
    } catch (err) {
      console.error("Error loading insights:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-bold text-gray-500 animate-pulse">Analyzing Talent Pool & Compiling Insights...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-3xl border border-red-100 max-w-xl mx-auto my-8">
        <h3 className="text-lg font-black text-red-800">Failed to Load Talent Intelligence</h3>
        <p className="text-xs text-red-600 mt-2">{error || "Insights data is unavailable."}</p>
        <button
          onClick={fetchInsights}
          className="mt-4 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition duration-200"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Pre-calculate summary statistics
  const totalTalent = data.locations.reduce((acc, curr) => acc + curr.count, 0);
  const medianCTC = data.salaryCurve.find(c => c.percentile === "50%")?.ctc || 0;
  const topHotspot = data.locations[0]?.name || "N/A";
  const immediatePct = data.noticePeriods.find(n => n.name === "Immediate" || n.name.includes("15"))?.percentage || 0;

  // Custom colors for charts
  const PIE_COLORS = ["#6366f1", "#4f46e5", "#3730a3", "#818cf8", "#a5b4fc", "#c7d2fe"];

  // Multi-dimensional Options (7 key dimensions from Naukri Talent Pulse)
  const dimensionOptions = [
    { label: "Location", value: "location" },
    { label: "Company", value: "company" },
    { label: "Designation", value: "designation" },
    { label: "Department", value: "department" },
    { label: "Industry", value: "industry" },
    { label: "Institute", value: "institute" },
    { label: "Course / Degree", value: "course" }
  ];

  // Generate combinations for selected dimensions A & B
  const getDimensionValues = (dim) => {
    if (dim === "location") return data.locations.map(l => l.name);
    if (dim === "company") return data.companies.map(c => c.name);
    if (dim === "designation") return data.designations ? data.designations.map(d => d.name) : [];
    if (dim === "department") return data.departments ? data.departments.map(d => d.name) : [];
    if (dim === "industry") return data.industries ? data.industries.map(i => i.name) : [];
    if (dim === "institute") return data.institutes ? data.institutes.map(i => i.name) : [];
    if (dim === "course") return data.courses ? data.courses.map(c => c.name) : [];
    return [];
  };

  const getDimensionLabel = (dimValue) => {
    return dimensionOptions.find(o => o.value === dimValue)?.label || "";
  };

  const valA = getDimensionValues(dimA);
  const valB = getDimensionValues(dimB);

  // Generate grid values dynamically based on selected dimensions
  const getMultiDimensionalData = () => {
    const list = [];
    if (!data.candidates) return [];

    const getCandidateField = (c, key) => {
      if (key === "location") return c.location;
      if (key === "company") return c.company;
      if (key === "designation") return c.designation;
      if (key === "department") return c.department;
      if (key === "industry") return c.industry;
      if (key === "institute") return c.institute;
      if (key === "course") return c.course;
      return "";
    };

    // Calculate Overall Median CTC from DB
    const overallCtcs = data.candidates
      .map(c => c.currentCtc)
      .filter(c => c !== null && c > 0)
      .sort((a, b) => a - b);
    const overallMedian = overallCtcs.length > 0 ? overallCtcs[Math.floor(overallCtcs.length * 0.5)] : 8.5;

    // Calculate relative salary index multiplier for Dimension A options
    const dimAIndexes = {};
    valA.forEach(itemA => {
      const itemCtcs = data.candidates
        .filter(c => getCandidateField(c, dimA) && getCandidateField(c, dimA).toLowerCase() === itemA.toLowerCase() && c.currentCtc !== null && c.currentCtc > 0)
        .map(c => c.currentCtc);
      
      if (itemCtcs.length > 0) {
        const avg = itemCtcs.reduce((sum, val) => sum + val, 0) / itemCtcs.length;
        dimAIndexes[itemA.toLowerCase()] = avg / overallMedian;
      } else {
        dimAIndexes[itemA.toLowerCase()] = 1.0;
      }
    });

    // Calculate relative salary index multiplier for Dimension B options
    const dimBIndexes = {};
    valB.forEach(itemB => {
      const itemCtcs = data.candidates
        .filter(c => getCandidateField(c, dimB) && getCandidateField(c, dimB).toLowerCase() === itemB.toLowerCase() && c.currentCtc !== null && c.currentCtc > 0)
        .map(c => c.currentCtc);
      
      if (itemCtcs.length > 0) {
        const avg = itemCtcs.reduce((sum, val) => sum + val, 0) / itemCtcs.length;
        dimBIndexes[itemB.toLowerCase()] = avg / overallMedian;
      } else {
        dimBIndexes[itemB.toLowerCase()] = 1.0;
      }
    });

    valA.slice(0, 5).forEach((itemA) => {
      valB.slice(0, 5).forEach((itemB) => {
        if (itemA === itemB) return;

        // Filter candidates matching both dimensions
        const matches = data.candidates.filter(c => {
          const valOfA = getCandidateField(c, dimA);
          const valOfB = getCandidateField(c, dimB);
          
          return valOfA && valOfB &&
                 String(valOfA).toLowerCase() === String(itemA).toLowerCase() && 
                 String(valOfB).toLowerCase() === String(itemB).toLowerCase();
        });

        // Compute actual salary statistics for this intersection
        const ctcs = matches
          .map(c => c.currentCtc)
          .filter(c => c !== null && c > 0)
          .sort((a, b) => a - b);

        let salaryRange = "N/A";
        let medianCTCStr = "N/A";

        if (ctcs.length > 0) {
          const median = ctcs[Math.floor(ctcs.length * 0.5)];
          const minVal = ctcs[0];
          const maxVal = ctcs[ctcs.length - 1];
          salaryRange = `${minVal.toFixed(1)}L - ${maxVal.toFixed(1)}L`;
          medianCTCStr = `${median.toFixed(1)} LPA`;
        } else {
          // Candidates exist but have no CTC, or 0 candidates exist.
          // Estimate CTC dynamically using Dimension A and B multipliers calculated from actual DB.
          const indexA = dimAIndexes[itemA.toLowerCase()] || 1.0;
          const indexB = dimBIndexes[itemB.toLowerCase()] || 1.0;
          const estimatedMedian = overallMedian * indexA * indexB;
          
          const minVal = estimatedMedian * 0.8;
          const maxVal = estimatedMedian * 1.2;
          
          salaryRange = `${minVal.toFixed(1)}L - ${maxVal.toFixed(1)}L`;
          medianCTCStr = `${estimatedMedian.toFixed(1)} LPA (Est)`;
        }

        list.push({
          dimValA: itemA,
          dimValB: itemB,
          salaryRange,
          medianCTC: medianCTCStr,
          candidateCount: matches.length
        });
      });
    });

    return list;
  };

  const matrixData = getMultiDimensionalData();

  const handleCellClick = (row) => {
    if (row.candidateCount === 0) return;
    const params = new URLSearchParams();
    
    const mapDimensionToParam = (dimKey, dimVal) => {
      if (dimKey === "location") return { key: "city", val: dimVal };
      if (dimKey === "company") return { key: "company", val: dimVal };
      if (dimKey === "designation") return { key: "designation", val: dimVal };
      if (dimKey === "department") return { key: "q", val: dimVal };
      if (dimKey === "industry") return { key: "q", val: dimVal };
      if (dimKey === "institute") return { key: "qualification", val: dimVal };
      if (dimKey === "course") return { key: "qualification", val: dimVal };
      return null;
    };

    const pA = mapDimensionToParam(dimA, row.dimValA);
    const pB = mapDimensionToParam(dimB, row.dimValB);

    if (pA) params.append(pA.key, pA.val);
    if (pB) params.append(pB.key, pB.val);

    navigate(`/dashboard/talentpool/results?${params.toString()}`);
  };

  return (
    <div className="w-full space-y-8 bg-slate-50/30 p-2 dark:bg-slate-950/20 font-sans">
      {/* 4 SUMMARY STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-indigo-100/5 bg-gradient-to-br from-white to-indigo-50/20 flex items-center gap-4 dark:bg-slate-900 dark:border-slate-800 dark:from-slate-900 dark:to-indigo-950/5">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center dark:bg-indigo-950/40 dark:text-indigo-400">
            <FaUserTie className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Active Talent Pool</p>
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 leading-none mt-1">{totalTalent}+</h3>
            <p className="text-[9px] font-semibold text-emerald-600 mt-1">▲ 14.2% YoY growth</p>
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-indigo-100/5 bg-gradient-to-br from-white to-emerald-50/20 flex items-center gap-4 dark:bg-slate-900 dark:border-slate-800 dark:from-slate-900 dark:to-emerald-950/5">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center dark:bg-emerald-950/40 dark:text-emerald-400">
            <FaMoneyBillWave className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Median Talent CTC</p>
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 leading-none mt-1">{medianCTC} LPA</h3>
            <p className="text-[9px] font-semibold text-gray-400 dark:text-gray-500 mt-1">Based on market active CVs</p>
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-indigo-100/5 bg-gradient-to-br from-white to-sky-50/20 flex items-center gap-4 dark:bg-slate-900 dark:border-slate-800 dark:from-slate-900 dark:to-sky-950/5">
          <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center dark:bg-sky-950/40 dark:text-sky-400">
            <FaMapMarkerAlt className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Sourcing Hotspot</p>
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 leading-none mt-1">{topHotspot}</h3>
            <p className="text-[9px] font-semibold text-sky-600 mt-1">Highest candidate concentration</p>
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-indigo-100/5 bg-gradient-to-br from-white to-amber-50/20 flex items-center gap-4 dark:bg-slate-900 dark:border-slate-800 dark:from-slate-900 dark:to-amber-950/5">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center dark:bg-amber-950/40 dark:text-amber-400">
            <FaClock className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Immediate Joiners</p>
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 leading-none mt-1">{immediatePct}%</h3>
            <p className="text-[9px] font-semibold text-amber-600 mt-1">Active within 15 days or less</p>
          </div>
        </div>
      </div>

      {/* CHARTS CONTAINER SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* CHART 1: SALARY CURVE */}
        <div className="bg-white border border-gray-100 rounded-[32px] p-6 shadow-2xl shadow-indigo-100/10 dark:bg-slate-900 dark:border-slate-800 flex flex-col space-y-4">
          <div>
            <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-500"></span>
              Salary Curve (CTC Percentiles)
            </h3>
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mt-0.5">Analyze what candidates earn across percentiles</p>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.salaryCurve} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCtc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:stroke-slate-800" />
                <XAxis dataKey="percentile" stroke="#94a3b8" fontSize={10} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} label={{ value: 'CTC (Lakhs)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#94a3b8', fontSize: 10, fontWeight: 'bold' } }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '11px' }}
                  formatter={(value) => [`${value} LPA`, 'Current CTC']}
                />
                <Area type="monotone" dataKey="ctc" stroke="#4f46e5" strokeWidth={3} fillOpacity={1} fill="url(#colorCtc)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-gray-400 dark:text-gray-500 italic bg-gray-50 dark:bg-slate-800/40 p-3 rounded-2xl">
            Median CTC (50th percentile) represents the midpoint of the market. 50% of the active talent pool earns less than {medianCTC} LPA.
          </div>
        </div>

        {/* CHART 2: SALARY TRENDS BY EXPERIENCE BANDS */}
        <div className="bg-white border border-gray-100 rounded-[32px] p-6 shadow-2xl shadow-indigo-100/10 dark:bg-slate-900 dark:border-slate-800 flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                Salary Trends by Experience Band
              </h3>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mt-0.5">Top 25%, Median, and Bottom 25% earners comparison</p>
            </div>

            {/* Toggle tabs for Median vs Distribution */}
            <div className="flex gap-1 p-1 bg-gray-50 dark:bg-slate-800/80 rounded-2xl border border-gray-150 dark:border-slate-700 max-w-[200px]">
              <button
                type="button"
                onClick={() => setTrendMode("median")}
                className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-wider rounded-xl transition duration-200 ${
                  trendMode === "median"
                    ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm border border-gray-150/40 dark:border-slate-600"
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                }`}
              >
                Median CTC
              </button>
              <button
                type="button"
                onClick={() => setTrendMode("distribution")}
                className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-wider rounded-xl transition duration-200 ${
                  trendMode === "distribution"
                    ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm border border-gray-150/40 dark:border-slate-600"
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                }`}
              >
                Distribution
              </button>
            </div>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data.salaryTrends.map(item => ({
                  ...item,
                  range: [item.bottom25, item.top25]
                }))}
                margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:stroke-slate-800" />
                <XAxis dataKey="experience" stroke="#94a3b8" fontSize={10} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '11px' }} />
                
                {trendMode === "median" ? (
                  <>
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', marginTop: '10px' }} />
                    <Bar dataKey="median" name="Median CTC" fill="#6366f1" radius={[8, 8, 0, 0]} barSize={25} />
                    <Line type="monotone" dataKey="top25" name="Top 25% Earner" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="bottom25" name="Bottom 25% Earner" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  </>
                ) : (
                  <>
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', marginTop: '10px' }} />
                    {/* Floating Bar for the Top and Bottom Tier Range */}
                    <Bar dataKey="range" name="CTC Range (Bottom 25% - Top 25%)" fill="#cbd5e1" barSize={3} radius={0} />
                    <Line type="monotone" dataKey="top25" name="Top 25% Earner" stroke="none" dot={{ r: 5, fill: '#ef4444' }} />
                    <Line type="monotone" dataKey="bottom25" name="Bottom 25% Earner" stroke="none" dot={{ r: 5, fill: '#10b981' }} />
                    <Line type="monotone" dataKey="median" name="Median CTC" stroke="#6366f1" strokeWidth={1.5} strokeDasharray="3 3" dot={{ r: 5, fill: '#6366f1' }} />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-gray-400 dark:text-gray-500 italic bg-gray-50 dark:bg-slate-800/40 p-3 rounded-2xl">
            Recruiter Tip: Standardize offers near the median for normal hires. Target top 25% percentiles specifically to outbid competition for premium niche profiles.
          </div>
        </div>

        {/* CHART 3: GEOGRAPHICAL HOTSPOTS */}
        <div className="bg-white border border-gray-100 rounded-[32px] p-6 shadow-2xl shadow-indigo-100/10 dark:bg-slate-900 dark:border-slate-800 flex flex-col space-y-4">
          <div>
            <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-sky-500"></span>
              Geographical Sourcing Hotspots
            </h3>
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mt-0.5">Top cities for sourcing candidate density</p>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart layout="vertical" data={data.locations} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" className="dark:stroke-slate-800" />
                <XAxis type="number" stroke="#94a3b8" fontSize={10} tickLine={false} />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={10} tickLine={false} width={80} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '11px' }} />
                <Area type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2} fill="#bae6fd" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CHART 4: NOTICE PERIOD DISTRIBUTION */}
        <div className="bg-white border border-gray-100 rounded-[32px] p-6 shadow-2xl shadow-indigo-100/10 dark:bg-slate-900 dark:border-slate-800 flex flex-col space-y-4">
          <div>
            <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
              Notice Period / Sourcing Speed
            </h3>
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mt-0.5">Understand how fast you can hire talent from the market</p>
          </div>
          <div className="h-64 w-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.noticePeriods}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="count"
                  nameKey="name"
                  label={({ name, percentage }) => `${name} (${percentage}%)`}
                >
                  {data.noticePeriods.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* NEW SECTION: COMPETITOR SOURCING PIPELINE */}
      <div className="bg-white border border-gray-150 rounded-[32px] p-6 shadow-2xl shadow-indigo-100/10 dark:bg-slate-900 dark:border-slate-800 flex flex-col space-y-4">
        <div>
          <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
            <FaBuilding className="text-indigo-600 dark:text-indigo-400" />
            Top Competitors Talent Pools & Compensation
          </h3>
          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mt-0.5">Analyze candidate counts and median CTCs across top hiring sources</p>
        </div>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.companies} margin={{ top: 20, right: 10, left: -25, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:stroke-slate-800" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} tickLine={false} />
              <YAxis yAxisId="left" stroke="#94a3b8" fontSize={10} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={10} tickLine={false} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '11px' }} />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Bar yAxisId="left" dataKey="candidates" name="Candidates Count" fill="#818cf8" radius={[6, 6, 0, 0]} barSize={30} />
              <Line yAxisId="right" type="monotone" dataKey="medianCtc" name="Median CTC (LPA)" stroke="#ef4444" strokeWidth={3} dot={{ r: 4 }} />
              <Line yAxisId="right" type="monotone" dataKey="expCtc" name="Expected CTC (LPA)" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* NEW SECTION: MULTI-DIMENSIONAL SALARY BENCHMARKING CALCULATOR */}
      <div className="bg-white border border-gray-150 rounded-[32px] p-8 shadow-2xl shadow-indigo-100/10 dark:bg-slate-900 dark:border-slate-800 space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-gray-150 dark:border-slate-800">
          <div>
            <h3 className="text-base font-black text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <FaCalculator className="text-indigo-600 dark:text-indigo-400" />
              Multi-Dimensional Salary Benchmarking
            </h3>
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mt-1">Cross-reference active talent dimensions to build custom benchmark guidelines</p>
          </div>
          
          {/* Dimension Selectors */}
          <div className="flex flex-wrap items-center gap-4 bg-gray-50 dark:bg-slate-800/60 p-3 rounded-2xl border border-gray-100 dark:border-slate-800">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Dimension 1:</span>
              <select
                value={dimA}
                onChange={(e) => setDimA(e.target.value)}
                className="bg-white border border-gray-200 rounded-xl px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                {dimensionOptions.map(opt => (
                  <option key={opt.value} value={opt.value} disabled={opt.value === dimB}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <FaSlidersH className="text-indigo-400 w-3 h-3 hidden sm:block" />

            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Dimension 2:</span>
              <select
                value={dimB}
                onChange={(e) => setDimB(e.target.value)}
                className="bg-white border border-gray-200 rounded-xl px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                {dimensionOptions.map(opt => (
                  <option key={opt.value} value={opt.value} disabled={opt.value === dimA}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Matrix Grid Representation */}
        <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-slate-800">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-indigo-50/40 dark:bg-indigo-950/20 border-b border-gray-150 dark:border-slate-800">
                <th className="px-6 py-4 text-xs font-black text-indigo-950 dark:text-indigo-400 uppercase tracking-wider">{getDimensionLabel(dimA)}</th>
                <th className="px-6 py-4 text-xs font-black text-indigo-950 dark:text-indigo-400 uppercase tracking-wider">{getDimensionLabel(dimB)}</th>
                <th className="px-6 py-4 text-xs font-black text-indigo-950 dark:text-indigo-400 uppercase tracking-wider">Market Salary Range</th>
                <th className="px-6 py-4 text-xs font-black text-indigo-950 dark:text-indigo-400 uppercase tracking-wider">Median CTC Benchmark</th>
                <th className="px-6 py-4 text-xs font-black text-indigo-950 dark:text-indigo-400 uppercase tracking-wider">Talent Pool Volume</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {matrixData.map((row, idx) => (
                <tr key={idx} className="hover:bg-indigo-50/10 dark:hover:bg-slate-800/25 transition">
                  <td className="px-6 py-3.5 text-xs font-extrabold text-slate-800 dark:text-slate-100">{row.dimValA}</td>
                  <td className="px-6 py-3.5 text-xs font-semibold text-gray-700 dark:text-gray-300">{row.dimValB}</td>
                  <td className="px-6 py-3.5 text-xs font-bold text-indigo-600 dark:text-indigo-400">{row.salaryRange}</td>
                  <td className="px-6 py-3.5 text-xs font-black text-slate-850 dark:text-slate-200">{row.medianCTC}</td>
                  <td className="px-6 py-3.5 text-xs font-extrabold">
                    {row.candidateCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleCellClick(row)}
                        title="Click to view candidates in Talent Pool"
                        className="px-2.5 py-1 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 rounded-full border border-indigo-100 dark:border-indigo-900/50 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white hover:border-indigo-600 transition duration-200 cursor-pointer shadow-sm active:scale-95"
                      >
                        {row.candidateCount} candidates
                      </button>
                    ) : (
                      <span className="px-2.5 py-1 bg-gray-50 text-gray-400 dark:bg-slate-800 dark:text-gray-500 rounded-full border border-gray-100 dark:border-slate-700/50 cursor-not-allowed">
                        {row.candidateCount} candidates
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
